import { memo, useState, useEffect } from "react";
import styled from "styled-components";
import { Button, Switch, Tooltip, Divider } from "antd";
import {
  ExpandOutlined,
  CompressOutlined,
  CloseOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { HiOutlineDatabase, HiOutlineUsers, HiOutlineMap } from "react-icons/hi";
import { PiCamera, PiImages } from "react-icons/pi";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IconHiDatabase = HiOutlineDatabase as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IconHiUsers = HiOutlineUsers as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IconHiMap = HiOutlineMap as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IconPiCamera = PiCamera as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IconPiImages = PiImages as any;
import { useAtomValue } from "jotai";
import { currentProjectAtom } from "~/stores/ProjectStore";
import CollectItemsMapUtils from "../../../utils/CollectItemsMapUtils";
import FreePhotoVectorTileUtils from "../../../utils/FreePhotoVectorTileUtils";
import CollectPhotoVectorTileUtils from "../../../utils/CollectPhotoVectorTileUtils";
import { MemberMapUtilOptimized } from "../../../Member/MemberMapUtilOptimized";
import LayerRow from "../components/LayerRow/LayerRow";
import AutoRefreshRow from "../components/LayerRow/AutoRefreshRow";
import LayerStyleModal, { LayerStyles } from "./LayerStyleModal";

interface LayerTabProps {
  onMaximize?: () => void;
  onClose?: () => void;
  isMaximized?: boolean;
}

const LayerTab = ({ onMaximize, onClose, isMaximized }: LayerTabProps) => {
  const currentProject = useAtomValue(currentProjectAtom);
  const hasProject = !!currentProject?.id;

  // --- Section states ---
  const [collectVisible, setCollectVisible] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [membersVisible, setMembersVisible] = useState(false);
  const [freePhotoVisible, setFreePhotoVisible] = useState(false);
  const [collectPhotoVisible, setCollectPhotoVisible] = useState(false);
  const [showStyleModal, setShowStyleModal] = useState(false);

  // Sync state với map layer visibility khi mount
  useEffect(() => {
    setCollectVisible(CollectItemsMapUtils.isLayerVisible());
    setAutoRefreshEnabled(CollectItemsMapUtils.isAutoRefreshEnabled());
    setMembersVisible(MemberMapUtilOptimized.isLayerVisible());
    setFreePhotoVisible(FreePhotoVectorTileUtils.isLayerVisible());
    setCollectPhotoVisible(CollectPhotoVectorTileUtils.isLayerVisible());
  }, []);

  // ---- Section 1: Dữ liệu thu thập ----
  const handleToggleCollect = (checked: boolean) => {
    if (checked) {
      CollectItemsMapUtils.addToMap(currentProject?.id);
    } else {
      CollectItemsMapUtils.removeFromMap();
      setAutoRefreshEnabled(false);
    }
    setCollectVisible(checked);
  };

  const handleRefreshCollect = () => {
    if (collectVisible) {
      CollectItemsMapUtils.refreshData();
    } else {
      CollectItemsMapUtils.removeFromMap();
      setTimeout(() => {
        CollectItemsMapUtils.addToMap(currentProject?.id);
        setCollectVisible(true);
      }, 100);
    }
  };

  const handleToggleAutoRefresh = (checked: boolean) => {
    CollectItemsMapUtils.setAutoRefreshEnabled(checked, 30);
    setAutoRefreshEnabled(checked);
  };

  const handleApplyStyles = (styles: LayerStyles) => {
    CollectItemsMapUtils.updateStyles(styles);
    if (collectVisible) {
      CollectItemsMapUtils.removeFromMap();
      setTimeout(() => {
        CollectItemsMapUtils.addToMap(currentProject?.id);
      }, 100);
    }
  };

  // ---- Section 2: Thành viên ----
  const handleToggleMembers = (checked: boolean) => {
    MemberMapUtilOptimized.setVisible(checked);
    setMembersVisible(checked);
  };

  // ---- Section 3: Ảnh tự do ----
  const handleToggleFreePhoto = (checked: boolean) => {
    if (checked) {
      FreePhotoVectorTileUtils.addToMap(currentProject?.id);
    } else {
      FreePhotoVectorTileUtils.removeFromMap();
    }
    setFreePhotoVisible(checked);
  };

  const handleRefreshFreePhoto = () => {
    FreePhotoVectorTileUtils.refreshData();
  };

  // ---- Section 4: Ảnh thu thập ----
  const handleToggleCollectPhoto = (checked: boolean) => {
    if (checked) {
      CollectPhotoVectorTileUtils.addToMap(currentProject?.id);
    } else {
      CollectPhotoVectorTileUtils.removeFromMap();
    }
    setCollectPhotoVisible(checked);
  };

  const handleRefreshCollectPhoto = () => {
    CollectPhotoVectorTileUtils.refreshData();
  };

  const disabledReason = hasProject ? undefined : "Chọn project để sử dụng tính năng này";

  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderTitle>
          <AppstoreOutlined style={{ marginRight: 6, color: "#64748b" }} />
          Hiển thị trên bản đồ
        </HeaderTitle>
        <HeaderActions>
          {onMaximize && (
            <Tooltip title={isMaximized ? "Thu nhỏ" : "Phóng to"}>
              <Button
                type="text"
                size="small"
                icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={onMaximize}
              />
            </Tooltip>
          )}
          {onClose && (
            <Tooltip title="Đóng">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
              />
            </Tooltip>
          )}
        </HeaderActions>
      </Header>

      {!hasProject && (
        <WarningMessage>Chọn project để hiển thị dữ liệu</WarningMessage>
      )}

      <SectionList>
        {/* Section 1: Dữ liệu thu thập */}
        <LayerRow
          icon={<IconHiDatabase />}
          title="Dữ liệu thu thập"
          isVisible={collectVisible}
          onToggle={handleToggleCollect}
          onRefresh={handleRefreshCollect}
          onSettings={() => setShowStyleModal(true)}
          disabled={!hasProject}
          disabledReason={disabledReason}
        >
          <AutoRefreshRow
            enabled={autoRefreshEnabled}
            onToggle={handleToggleAutoRefresh}
            intervalSeconds={30}
            disabled={!collectVisible}
          />
        </LayerRow>

        <Divider style={{ margin: "6px 0" }} />

        {/* Section 2: Thành viên */}
        <LayerRow
          icon={<IconHiUsers />}
          title="Thành viên"
          isVisible={membersVisible}
          onToggle={handleToggleMembers}
          disabled={!hasProject}
          disabledReason={disabledReason}
        />

        <Divider style={{ margin: "6px 0" }} />

        {/* Section 3: Ảnh tự do */}
        <LayerRow
          icon={<IconPiCamera />}
          title="Ảnh tự do"
          isVisible={freePhotoVisible}
          onToggle={handleToggleFreePhoto}
          onRefresh={handleRefreshFreePhoto}
          disabled={!hasProject}
          disabledReason={disabledReason}
        />

        <Divider style={{ margin: "6px 0" }} />

        {/* Section 4: Ảnh thu thập */}
        <LayerRow
          icon={<IconPiImages />}
          title="Ảnh thu thập"
          isVisible={collectPhotoVisible}
          onToggle={handleToggleCollectPhoto}
          onRefresh={handleRefreshCollectPhoto}
          disabled={!hasProject}
          disabledReason={disabledReason}
        />

        <Divider style={{ margin: "6px 0" }} />

        {/* Section 5: Các lớp bản đồ */}
        <MapDataSection>
          <MapDataHeader>
            <MapDataHeaderLeft>
              <IconHiMap style={{ fontSize: 15, color: "#64748b" }} />
              <MapDataTitle>Các lớp bản đồ</MapDataTitle>
            </MapDataHeaderLeft>
          </MapDataHeader>
          <MapDataNote>
            Quản lý các lớp bản đồ tại{" "}
            <HintLink>tab Thư viện</HintLink>
          </MapDataNote>
        </MapDataSection>
      </SectionList>

      <LayerStyleModal
        open={showStyleModal}
        onClose={() => setShowStyleModal(false)}
        onApply={handleApplyStyles}
      />
    </Container>
  );
};

export default memo(LayerTab);

// ---- Styled Components ----

const Container = styled.div`
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 8px 8px;
`;

const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const WarningMessage = styled.div`
  margin-bottom: 8px;
  padding: 7px 12px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  font-size: 12px;
  color: #92400e;
  text-align: center;
`;

const MapDataSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MapDataHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
`;

const MapDataHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MapDataTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`;

const MapDataNote = styled.div`
  padding: 5px 14px;
  font-size: 11px;
  color: #94a3b8;
`;

const HintLink = styled.span`
  color: #3b82f6;
  text-decoration: underline;
  cursor: default;
`;
